import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mic, Send, Square, Loader2, ArrowUp } from "lucide-react";
import { useAudioRecording } from "@/hooks/useAudioRecording";
import { ChatActions } from "@/components/ChatActions";
import { clearFormDraft, readFormDraft, writeFormDraft } from "@/lib/formDraftStorage";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  onRefreshChat?: () => void;
  onUploadResume?: (file: File) => void | Promise<boolean | void>;
  userProfile?: { subscription_tier?: string } | null;
  disabled?: boolean;
  isConversationComplete?: boolean;
  onAddLoadingMessage?: (message: string) => string | null;
  onRemoveLoadingMessage?: (messageId: string) => void;
  onOpenDriveSearch?: () => void;
}

export function ChatInput({
  onSendMessage,
  isLoading = false,
  placeholder = "Type here...",
  onRefreshChat,
  onUploadResume,
  userProfile,
  disabled = false,
  isConversationComplete = false,
  onAddLoadingMessage,
  onRemoveLoadingMessage,
  onOpenDriveSearch,
}: ChatInputProps) {
  const { text, isRecording, isProcessing, timeLeft, totalTime, recordingStage, startRecording, stopRecording, stopAndReturnText } = useAudioRecording({ userProfile });
  const [message, setMessage] = useState(() => readFormDraft('chatInput', ""));
  const [sendingVoice, setSendingVoice] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    writeFormDraft('chatInput', message);
  }, [message]);

  // Handle sending message
  const handleSend = () => {
    if (message.trim() && !disabled && !isLoading) {
      onSendMessage(message);
      clearFormDraft('chatInput');
      setMessage("");
      if (textareaRef.current) {
        textareaRef.current.style.height = '44px';
      }
    }
  };

  // Stop recording, drop transcript into textarea (existing behavior)
  const handleStopToTextarea = useCallback(async () => {
    await stopRecording();
  }, [stopRecording]);

  // Stop recording and immediately send the transcript as a chat message,
  // bypassing the textarea entirely.
  const handleStopAndSend = useCallback(async () => {
    setSendingVoice(true);
    try {
      const transcript = (await stopAndReturnText()).trim();
      if (transcript) {
        onSendMessage(transcript);
        clearFormDraft('chatInput');
      }
    } finally {
      setSendingVoice(false);
    }
  }, [stopAndReturnText, onSendMessage]);

  // Handle microphone button click (start recording when idle)
  const handleMicClick = async () => {
    if (!isRecording) {
      startRecording();
    }
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      const wordCount = message.trim().split(/\s+/).filter(w => w).length;
      if (wordCount > 15) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 140) + 'px';
      } else {
        textareaRef.current.style.height = '44px';
      }
    }
  }, [message]);


  // auto append the transcribed text to the message
  useEffect(() => {
    if (text) {
      setMessage(prev => prev + (prev ? ' ' : '') + text);
    }
  }, [text]);

  const wordCount = message.trim().split(/\s+/).filter(w => w).length;
  const showScrollbar = wordCount > 150;

  return (
    <div className="flex items-end gap-2 p-2 bg-card/80 backdrop-blur-sm border-t border-border sm:p-3">
      {onRefreshChat && onUploadResume && (
        <ChatActions
          onRefreshChat={onRefreshChat}
          onUploadResume={onUploadResume}
          userProfile={userProfile}
          isConversationComplete={isConversationComplete}
          onAddLoadingMessage={onAddLoadingMessage}
          onRemoveLoadingMessage={onRemoveLoadingMessage}
          onOpenDriveSearch={onOpenDriveSearch}
        />
      )}
      <Textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
        placeholder={isProcessing ? "Transcribing..." : placeholder}
        disabled={disabled || isLoading || isProcessing}
        className={`min-h-11 flex-1 resize-none bg-input border-input-border py-2 text-sm ${showScrollbar ? 'overflow-y-auto' : 'overflow-hidden'}`}
        style={{ maxHeight: '140px' }}
      />
      <div className="flex shrink-0 items-end gap-1">
        <div className="relative flex items-center gap-1">
          <Button
            onClick={isRecording ? handleStopToTextarea : handleMicClick}
            variant="outline"
            size="icon"
            disabled={disabled || isLoading || recordingStage === 'processing' || sendingVoice}
            className={`h-9 w-9 ${isRecording ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`}
            aria-label={isRecording ? "Stop and place transcript in input" : "Start voice recording"}
            title={isRecording ? "Stop & edit before sending" : "Start voice recording"}
          >
            {isProcessing && !sendingVoice ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isRecording ? (
              <Square className="h-4 w-4" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </Button>

          {isRecording && (
            <Button
              onClick={handleStopAndSend}
              size="icon"
              disabled={disabled || isLoading || sendingVoice}
              className="h-9 w-9 bg-primary text-primary-foreground hover:bg-primary/90"
              aria-label="Stop and send transcript immediately"
              title="Stop & send"
            >
              {sendingVoice ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </Button>
          )}

          {isRecording && (
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-destructive text-destructive-foreground px-2 py-1 rounded text-xs font-medium whitespace-nowrap">
              {userProfile?.subscription_tier === 'pro'
                ? `${Math.floor(totalTime / 60)}:${(totalTime % 60).toString().padStart(2, '0')}`
                : `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`}
            </div>
          )}
        </div>
        <Button
          onClick={handleSend}
          size="icon"
          disabled={!message.trim() || disabled || isLoading || recordingStage === 'processing'}
          className="h-9 w-9"
          aria-label="Send message"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
